const MAX_WATCHLIST_SYMBOLS = 100;

function isPlainRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function own(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * JSON values from Mongo and localStorage can differ only in object key order
 * or numeric representation. Treat those as the same sync value so an
 * unchanged nested preference never becomes a false local mutation.
 */
export function syncValuesEqual(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return Number(left) === Number(right);
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => syncValuesEqual(value, right[index]))
    );
  }
  if (isPlainRecord(left) || isPlainRecord(right)) {
    if (!isPlainRecord(left) || !isPlainRecord(right)) return false;
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every((key) => own(right, key) && syncValuesEqual(left[key], right[key]))
    );
  }
  return Object.is(left, right);
}

export function buildRecordPatch(current, baseline, allowedKeys) {
  if (!isPlainRecord(current)) return {};
  const previous = isPlainRecord(baseline) ? baseline : null;
  const allowed = Array.isArray(allowedKeys) ? new Set(allowedKeys) : null;
  const patch = {};
  for (const key of Object.keys(current)) {
    if (allowed && !allowed.has(key)) continue;
    if (!previous || !own(previous, key) || !syncValuesEqual(current[key], previous[key])) {
      patch[key] = current[key];
    }
  }
  return patch;
}

export function applyRecordPatch(baseline, patch) {
  const next = isPlainRecord(baseline) ? { ...baseline } : {};
  if (!isPlainRecord(patch)) return next;
  for (const [key, value] of Object.entries(patch)) next[key] = value;
  return next;
}

export function syncTimestampEpochMs(value) {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value !== "string" || !value.trim()) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * A pull is reconciled against the last cloud ancestor. Omission preserves a
 * prior value; explicit null remains a value. Disjoint edits compose. When
 * both sides changed the same key, the Android sync contract resolves it by
 * the domain clocks, with the remote value winning ties.
 */
export function reconcileRecordPull({
  current,
  baseline,
  remote,
  allowedKeys,
  localUpdatedAt,
  remoteUpdatedAt,
  preferRemote = false,
}) {
  const local = isPlainRecord(current) ? current : {};
  const incoming = isPlainRecord(remote) ? remote : {};
  const hadBaseline = isPlainRecord(baseline);
  const previous = hadBaseline ? baseline : {};
  const allowed = Array.isArray(allowedKeys) ? new Set(allowedKeys) : null;
  const localClock = syncTimestampEpochMs(localUpdatedAt);
  const remoteClock = syncTimestampEpochMs(remoteUpdatedAt);
  const nextBaseline = hadBaseline
    ? applyRecordPatch(baseline, incoming)
    : applyRecordPatch(local, incoming);
  const resolved = { ...nextBaseline };
  const localPatch = {};
  const remoteWinnerKeys = [];
  const localWinnerKeys = [];

  for (const key of Object.keys(local)) {
    if (allowed && !allowed.has(key)) continue;
    const baselineHasKey = own(previous, key);
    const remoteHasKey = own(incoming, key);
    const localChanged = hadBaseline
      ? !baselineHasKey || !syncValuesEqual(local[key], previous[key])
      : localClock > 0;
    const remoteChanged =
      remoteHasKey && (!baselineHasKey || !syncValuesEqual(incoming[key], previous[key]));
    const remoteWins =
      remoteHasKey &&
      (preferRemote ||
        (!hadBaseline
          ? !localChanged || remoteClock >= localClock
          : localChanged && remoteChanged
            ? remoteClock >= localClock
            : remoteChanged));

    if (remoteWins) {
      resolved[key] = incoming[key];
      remoteWinnerKeys.push(key);
    } else if (localChanged) {
      resolved[key] = local[key];
      localPatch[key] = local[key];
      localWinnerKeys.push(key);
    }
  }

  return {
    resolved,
    baseline: nextBaseline,
    pendingPatch: localPatch,
    remoteWinnerKeys,
    localWinnerKeys,
  };
}

export function normalizeWatchlistSymbols(values) {
  if (!Array.isArray(values)) return [];
  const symbols = [];
  const seen = new Set();
  for (const raw of values) {
    const symbol = String(raw || "")
      .trim()
      .toUpperCase()
      .slice(0, 80);
    if (!symbol || seen.has(symbol)) continue;
    seen.add(symbol);
    symbols.push(symbol);
    if (symbols.length >= MAX_WATCHLIST_SYMBOLS) break;
  }
  return symbols;
}

export function buildWatchlistPatch(current, baseline) {
  const local = normalizeWatchlistSymbols(current);
  const previous = normalizeWatchlistSymbols(baseline);
  const localSet = new Set(local);
  const previousSet = new Set(previous);
  return {
    added: local.filter((symbol) => !previousSet.has(symbol)),
    removed: previous.filter((symbol) => !localSet.has(symbol)),
  };
}

export function applyWatchlistPatch(baseline, patch) {
  const previous = normalizeWatchlistSymbols(baseline);
  const added = normalizeWatchlistSymbols(patch?.added);
  const removed = new Set(normalizeWatchlistSymbols(patch?.removed));
  const resolved = previous.filter((symbol) => !removed.has(symbol));
  const seen = new Set(resolved);
  for (const symbol of added) {
    if (removed.has(symbol) || seen.has(symbol)) continue;
    seen.add(symbol);
    resolved.push(symbol);
  }
  return resolved.slice(0, MAX_WATCHLIST_SYMBOLS);
}

export function reconcileWatchlistPull({
  current,
  baseline,
  remote,
  localUpdatedAt,
  remoteUpdatedAt,
  preferRemote = false,
}) {
  const local = normalizeWatchlistSymbols(current);
  const incoming = normalizeWatchlistSymbols(remote);
  if (!Array.isArray(baseline)) {
    const localWins =
      !preferRemote && syncTimestampEpochMs(localUpdatedAt) > syncTimestampEpochMs(remoteUpdatedAt);
    const pendingPatch = localWins
      ? buildWatchlistPatch(local, incoming)
      : { added: [], removed: [] };
    return {
      resolved: localWins ? local : incoming,
      baseline: incoming,
      pendingPatch,
    };
  }
  const pendingPatch = buildWatchlistPatch(local, baseline);
  return {
    resolved: applyWatchlistPatch(incoming, pendingPatch),
    baseline: incoming,
    pendingPatch,
  };
}

export function hasRecordEntries(value) {
  return isPlainRecord(value) && Object.keys(value).length > 0;
}

export function hasWatchlistChanges(value) {
  return (
    isPlainRecord(value) &&
    (normalizeWatchlistSymbols(value.added).length > 0 ||
      normalizeWatchlistSymbols(value.removed).length > 0)
  );
}

function normalizeDomainSyncState(value) {
  const parsed = isPlainRecord(value) ? value : {};
  return {
    localUpdatedAtEpochMs: syncTimestampEpochMs(parsed.localUpdatedAtEpochMs),
    remoteUpdatedAtEpochMs: syncTimestampEpochMs(parsed.remoteUpdatedAtEpochMs),
    acknowledgedAtEpochMs: syncTimestampEpochMs(parsed.acknowledgedAtEpochMs),
    pending: parsed.pending === true,
  };
}

export function normalizeSyncV2Baseline(value) {
  const parsed = isPlainRecord(value) ? value : {};
  const hasLegacyRemoteAncestor =
    !own(parsed, "remoteBaselineReady") &&
    (isPlainRecord(parsed.preferences) ||
      isPlainRecord(parsed.theme) ||
      Array.isArray(parsed.watchlist));
  return {
    version: 2,
    remoteBaselineReady: parsed.remoteBaselineReady === true || hasLegacyRemoteAncestor,
    ...(isPlainRecord(parsed.preferences) ? { preferences: parsed.preferences } : {}),
    ...(isPlainRecord(parsed.theme) ? { theme: parsed.theme } : {}),
    ...(Array.isArray(parsed.watchlist)
      ? { watchlist: normalizeWatchlistSymbols(parsed.watchlist) }
      : {}),
    domains: {
      preferences: normalizeDomainSyncState(parsed.domains?.preferences),
      theme: normalizeDomainSyncState(parsed.domains?.theme),
      watchlist: normalizeDomainSyncState(parsed.domains?.watchlist),
    },
  };
}

/**
 * Freeze the local state that existed before an account's first cloud pull.
 *
 * That snapshot is a three-way-merge ancestor, not a cloud acknowledgement.
 * A user edit made while the first pull is in flight can then be detected
 * per key. Without it, one domain timestamp makes every local default look
 * edited and can overwrite unrelated Android preferences.
 */
export function captureSyncV2LocalAncestors(value, snapshots) {
  const baseline = normalizeSyncV2Baseline(value);
  const source = isPlainRecord(snapshots) ? snapshots : {};
  if (!isPlainRecord(baseline.preferences) && isPlainRecord(source.preferences)) {
    baseline.preferences = { ...source.preferences };
  }
  if (!isPlainRecord(baseline.theme) && isPlainRecord(source.theme)) {
    baseline.theme = { ...source.theme };
  }
  if (!Array.isArray(baseline.watchlist) && Array.isArray(source.watchlist)) {
    baseline.watchlist = normalizeWatchlistSymbols(source.watchlist);
  }
  return baseline;
}

export function markSyncV2RemoteBaselineReady(value) {
  const baseline = normalizeSyncV2Baseline(value);
  baseline.remoteBaselineReady = true;
  return baseline;
}

export function isSyncV2RemoteBaselineReady(value) {
  return normalizeSyncV2Baseline(value).remoteBaselineReady;
}

export function markSyncV2PendingDomains(value, options, updatedAtEpochMs) {
  const baseline = normalizeSyncV2Baseline(value);
  const opts = isPlainRecord(options) ? options : {};
  const generalPush = Object.keys(opts).length === 0;
  const domains = [];
  if (generalPush || opts.includePreferences === true || opts.includeWatchlist === true) {
    domains.push("preferences");
  }
  if (generalPush || opts.includeTheme === true) domains.push("theme");
  if (opts.includeWatchlist === true) domains.push("watchlist");
  const timestamp = Math.max(1, syncTimestampEpochMs(updatedAtEpochMs) || Date.now());
  for (const domain of domains) {
    baseline.domains[domain].pending = true;
    baseline.domains[domain].localUpdatedAtEpochMs = Math.max(
      baseline.domains[domain].localUpdatedAtEpochMs,
      timestamp,
    );
  }
  return baseline;
}

export function pendingSyncV2Options(value) {
  const baseline = normalizeSyncV2Baseline(value);
  return {
    ...(baseline.domains.preferences.pending ? { includePreferences: true } : {}),
    ...(baseline.domains.theme.pending ? { includeTheme: true } : {}),
    ...(baseline.domains.watchlist.pending
      ? { includeWatchlist: true, includePreferences: true }
      : {}),
  };
}

export function shouldApplySyncAcknowledgement(domainState, acknowledgedAt) {
  const state = normalizeDomainSyncState(domainState);
  const timestamp = syncTimestampEpochMs(acknowledgedAt);
  return timestamp > 0 && timestamp >= state.acknowledgedAtEpochMs;
}
